import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { buildAutocompleteFilter } from "../buildAutocompleteFilter";
import { Account } from "../../../../../models/account";
import { Controller, FieldValues, Path, UseFormReturn, FieldName, FieldErrors } from "react-hook-form";
import { DocumentStructure } from "../../../../../models/document-structure";
import { ErrorMessage, FieldValuesFromFieldErrors } from "@hookform/error-message"
import { CssField } from "../../MuiInput.styled";
import { getValueByPath } from "@shared/lib/utils";
import { AccountHebType } from "../../../../../models/types";
import { AccountStructure } from "../../../../../models/accounts-structure";
import { useLazySearchAccountsBrowseQuery } from "@features/tickets/api/accounts.api";

interface AccountSearchProps<T extends FieldValues> {
    accountHebType: AccountHebType, // account hebrew type
    fieldStructure: AccountStructure | DocumentStructure, // field data
    useForm: UseFormReturn<T>; // form
    onSelect: (object: Account) => void, // update the form after object selected
    prefixPath?: string, // prefix path for the field in the form
    disabled?: boolean, // disable the field
}

function AccountSearch<T extends FieldValues>({accountHebType, fieldStructure, useForm, onSelect, prefixPath, disabled}: AccountSearchProps<T>) {
    const [triggerSearchAccounts] = useLazySearchAccountsBrowseQuery();

    
    const formPath = (prefixPath? prefixPath + "." : "" ) + fieldStructure.field_name;

    const [options, setOptions] = useState<Account[]>([]);
    const [inputValue, setInputValue] = useState("");
    

    useEffect(() => {

        if (inputValue.length < 3) {
            setOptions([]);
            return;
        }

        let cancelled = false;

        (async () => {
            try {

                const path = fieldStructure.field_name;
                const accounts = await fetchAccounts(accountHebType, path as Path<T>, inputValue);

                if (!cancelled) {
                    setOptions(accounts);
                }
            } 
            
            catch (e) {
                if (!cancelled) {
                    console.error(e);
                }
            }
        })();

        return () => {
            cancelled = true;
        };

    }, [inputValue, accountHebType, fieldStructure.field_name]);


    /**
     * search accounts by key and value
     * @param accountHebType - the type in hebrew of the account
     * @param key - account key name 
     * @param value - value for search
     * @returns accounts
     */
    async function fetchAccounts(accountHebType: AccountHebType, key: Path<T>, value: string): Promise<Account[]> {

        const response = await triggerSearchAccounts({
            accountType: accountHebType,
            filter: buildAutocompleteFilter(String(key), value),
            include: ["customData"],
            per_page: 5,
            sort: ["id"],
        }).unwrap();

        return response.data;
    }


    /**
     * update the form with a new value
     * @param val - the new value, can be a specific field value or the whole object itself
     */
    function onChange(val: string | Account | null): void {

        if (typeof val === "string") {
            useForm.setValue(formPath as  Path<T>, val as any);
            return;
        }

        if (val === null) {
            useForm.setValue(formPath as  Path<T>, null as any); // or reset relevant fields
            return;
        }

        onSelect(val);
    }


    function renderOption(option: Account): string {

        if(fieldStructure.field_name === "account_number"){
            return String(option["account_number" as keyof Account]+ ": " + option["account_name" as keyof Account]);
        }

        return String(option["account_number" as keyof Account]+ ": " + String(getValueByPath(option, fieldStructure.field_name as keyof Account)));
    }


    function getValueByFieldName(account: Account): string {
        const path = fieldStructure.field_name;
        const value = getValueByPath(account, path)
        return String(value);
    }

    return (
        <CssField sx={{ minWidth: 200 }} error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}>
            <Controller
                name={formPath as Path<T>}
                control={useForm.control}
                render={({ field }) => (
                    <Autocomplete<Account, false, false, true>
                    {...field}
                    freeSolo
                    clearOnBlur={false}
                    options={options}
                    value={null}
                    getOptionLabel={(option) =>typeof option === "string" ? option : getValueByFieldName(option)}
                    inputValue={String(field.value ?? "")}
                    onChange={(_, newValue)=> onChange(newValue)}
                    disabled={disabled}

                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {renderOption(option)}
                        </li>
                    )}

                    onInputChange={(_, newInput) => {
                        field.onChange(newInput);
                        setInputValue(newInput);
                    }}

                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={fieldStructure.field_display_name}
                            error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}
                            helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldStructure.field_name as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                        />
                    )}
                    />
                )}
            />
        </CssField>
        
    );
}

export default AccountSearch;